import { memo, type CSSProperties, type MouseEvent } from 'react';
import { IconDots } from '@tabler/icons-react';
import { formatSmartDate, formatTimestamp } from '@/shared/utils/date';
import type { PlaylistGridRow as PlaylistGridRowType } from '@/lib/types/Playlist';
import styles from './PlaylistGrid.module.css';

interface PlaylistGridRowProps {
  playlist: PlaylistGridRowType;
  index: number;
  isSelected: boolean;
  onSelectOnly: (id: number, index: number) => void;
  onToggle: (id: number, index: number) => void;
  onRangeSelect: (id: number, index: number) => void;
  onMenuOpen: (index: number, buttonEl: HTMLElement) => void;
  onNavigate: (href: string) => void;
  style: CSSProperties;
}

function PlaylistGridRowComponent({
  playlist,
  index,
  isSelected,
  onSelectOnly,
  onToggle,
  onRangeSelect,
  onMenuOpen,
  onNavigate,
  style,
}: PlaylistGridRowProps) {
  const itemCountDisplay = playlist.itemCount == null
    ? '\u2014'
    : playlist.listType === 'tag'
      ? `~${playlist.itemCount} items`
      : `${playlist.itemCount} items`;

  const href = `/playlists/${playlist.displayId}`;

  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;

    if (target.closest('[data-cell="checkbox"]')) {
      onToggle(playlist.listId, index);
      return;
    }

    if (target.closest('[data-cell="actions"]')) {
      return;
    }

    if (target.closest('[data-cell="title"] a')) {
      return;
    }

    if (e.shiftKey) {
      e.preventDefault();
      onRangeSelect(playlist.listId, index);
    } else if (e.ctrlKey || e.metaKey) {
      onToggle(playlist.listId, index);
    } else {
      onSelectOnly(playlist.listId, index);
    }
  };

  const handleTitleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      onNavigate(href);
    }
  };

  return (
    <div
      className={`${styles.row} ${styles.gridColumns} ${isSelected ? styles.rowSelected : ''}`}
      style={style}
      onClick={handleClick}
      data-selected={isSelected}
      data-playlist-grid-row="true"
    >
      <div className={`${styles.cell} ${styles.cellCheckbox}`} data-cell="checkbox">
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            checked={isSelected}
            readOnly
            tabIndex={-1}
            aria-label={`Select ${playlist.title}`}
            onClick={(e) => e.stopPropagation()}
          />
          <span className={styles.checkboxControl} />
        </label>
      </div>

      <div className={`${styles.cell} ${styles.cellTitle}`} data-cell="title">
        <div className={styles.titleContainer}>
          <a href={href} className={styles.titleLink} onClick={handleTitleClick}>
            {playlist.title}
          </a>
          <span className={styles.titleId}>{playlist.displayId}</span>
        </div>
      </div>

      <div className={`${styles.cell} ${styles.cellType}`}>
        <span className={styles.typeBadge} data-kind={playlist.listType}>{playlist.listTypeLabel}</span>
        <span className={styles.typeMeta}>{itemCountDisplay}</span>
      </div>

      <div className={`${styles.cell} ${styles.cellStatus}`}>
        <span className={styles.statusBadge} data-color={playlist.isActive ? 'green' : 'gray'}>
          {playlist.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className={`${styles.cell} ${styles.cellDate}`}>
        <span title={formatTimestamp(playlist.created)}>{formatSmartDate(playlist.created)}</span>
      </div>

      <div className={`${styles.cell} ${styles.cellDate}`}>
        <span title={formatTimestamp(playlist.updated)}>{formatSmartDate(playlist.updated)}</span>
      </div>

      <div className={`${styles.cell} ${styles.cellAction}`} data-cell="actions">
        <button
          className={styles.kebabButton}
          onClick={(e) => {
            e.stopPropagation();
            onMenuOpen(index, e.currentTarget);
          }}
          aria-label="Actions"
          type="button"
        >
          <IconDots size={16} />
        </button>
      </div>
    </div>
  );
}

export const PlaylistGridRow = memo(PlaylistGridRowComponent, (prev, next) => {
  return (
    prev.playlist === next.playlist &&
    prev.index === next.index &&
    prev.isSelected === next.isSelected &&
    prev.style.transform === next.style.transform &&
    prev.style.height === next.style.height
  );
});
